import { useFacets, formatActiveRangeLabel } from "../../hooks/useFacets";
import { Form } from "formik";
import Button, { BUTTON_VARIANTS, BUTTON_SIZES } from "app/pages/.shared/form/Button";
import { FormattedMessage } from "react-intl";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import IconBack from "app/pages/.shared/static/icons/IconBack";
import { FilterCollapsible } from "../mobile/FilterCollapsible";
import Separator from "app/pages/.shared/Separator";
import { Fragment, useState } from "react";
import { FilterChip } from "./FilterChip";
import { useFilterContext } from "../../contexts/FilterProvider";
import PropTypes from "prop-types";
import { FilterDrawerFormPanel } from "./FilterDrawerFormPanel";
import "./FilterDrawerForm.scss";

export const FilterDrawerForm = ({
	isSinglePanelMode,
	selectedFilters,
	onReset,
	onChange,
	onOpenPanel,
	onApply,
}) => {
	const { closeDrawer } = useFilterContext();
	const { facets, filteredProducts } = useFacets(selectedFilters);

	const resultsCount = filteredProducts?.length;

	const expanded = [facets?.[0]?.facetId || null];
	const [expandedPanels, setExpandedPanels] = useState(isSinglePanelMode ? null : expanded);

	const handlePanelChange = key => {
		if (isSinglePanelMode) {
			if (key && onOpenPanel) {
				onOpenPanel(key);
			}

			setExpandedPanels(prevKey => (prevKey === key ? null : key));
		} else {
			const newKeys = Array.isArray(key) ? key : [key];

			const clickedKey = newKeys?.find(k => !expandedPanels?.includes(k));

			if (clickedKey && onOpenPanel) {
				onOpenPanel(clickedKey);
			}

			setExpandedPanels(newKeys);
		}
	};

	const handleDeleteFilterOption = (filterField, value) => {
		const currentValues = selectedFilters?.[filterField] || [];
		const newValues = currentValues.filter(currentValue => currentValue !== value);
		onChange(filterField, newValues);
	};

	const handleApply = () => {
		if (typeof onApply === "function") {
			onApply(selectedFilters, resultsCount);
		}
	};

	const buildSelectedFiltersWithLabels = (selectedFilters, facets) => {
		const result = {};

		Object.entries(selectedFilters).forEach(([filterId, values]) => {
			if (!values.length) {
				result[filterId] = [];
				return;
			}

			const matchingFacet = facets.find(facet =>
				facet.filters.some(filter => filter.field === filterId)
			);

			const matchingFilter = matchingFacet?.filters.find(filter => filter.field === filterId);

			if (!matchingFilter) {
				result[filterId] = [];
				return;
			}

			result[filterId] = values.map(value => {
				const option = matchingFilter.options.find(opt => opt.value === value);

				let label = option?.label;

				const isRange = value.startsWith("between_");

				if (!label && isRange) {
					label = formatActiveRangeLabel(matchingFilter, value);
				}

				return {
					value,
					label: label ?? value,
				};
			});
		});

		return result;
	};

	const selectedFiltersWithLabels = buildSelectedFiltersWithLabels(selectedFilters, facets);

	const hasSelectedFilters = Object.values(selectedFiltersWithLabels).some(
		values => Array.isArray(values) && values.length > 0
	);

	return (
		<Form className="filter-drawer-form">
			<header className="filter-drawer-form__header">
				<Button
					onClick={closeDrawer}
					icon={<IconBack size={20} />}
					className="filter-drawer-form__close-button"
				/>

				<Typography
					variant={TYPOGRAPHY_VARIANTS.XL}
					component="h2"
					isBold
					className="filter-drawer-form__title"
				>
					<FormattedMessage id="filter.mobile.button.label" />
				</Typography>

				{hasSelectedFilters && (
					<Button
						onClick={onReset}
						className="filter-drawer-form__reset-button"
						variant={BUTTON_VARIANTS.BORDERLESS}
					>
						<Typography
							variant={TYPOGRAPHY_VARIANTS.REGULAR}
							component="span"
							isBold
							className="filter-drawer-form__reset-button"
						>
							<FormattedMessage id="general.filter.raz.label" />
						</Typography>
					</Button>
				)}
			</header>

			<section className="filter-drawer-form__body">
				{hasSelectedFilters && (
					<>
						<ul className="filter-drawer-form__selected-filters">
							{Object.entries(selectedFiltersWithLabels).map(
								([filterId, filters]) => {
									return filters.map(filter => {
										return (
											<li
												key={`${filterId}-${filter.value}`}
												className="filter-drawer-form__selected-filter"
											>
												<FilterChip
													label={filter.label}
													onRemove={() =>
														handleDeleteFilterOption(
															filterId,
															filter.value
														)
													}
												/>
											</li>
										);
									});
								}
							)}
						</ul>

						<Separator design="full" />
					</>
				)}

				<FilterCollapsible
					accordion={isSinglePanelMode}
					activeKey={expandedPanels}
					onChange={handlePanelChange}
				>
					{facets.map((facet, index) => {
						const selectedFiltersCount = Object.entries(selectedFilters).reduce(
							(acc, [filterId, value]) => {
								if (facet.filters.some(filter => filter.field === filterId)) {
									return acc + (Array.isArray(value) ? value.length : 0);
								}
								return acc;
							},
							0
						);

						return (
							<Fragment key={facet.facetId}>
								<FilterDrawerFormPanel
									key={facet.facetId}
									facetId={facet.facetId}
									facetTitle={facet.facetTitle}
									filters={facet.filters}
									selectedFiltersCount={selectedFiltersCount}
								/>

								{index < Object.entries(facets).length - 1 && (
									<Separator design="full" />
								)}
							</Fragment>
						);
					})}
				</FilterCollapsible>
			</section>

			<footer className="filter-drawer-form__footer">
				<Button
					type="submit"
					className="filter-drawer-form__submit-button"
					size={BUTTON_SIZES.LARGE}
					onClick={handleApply}
					data-testid="filter-drawer-form-apply-button"
				>
					<Typography
						variant={TYPOGRAPHY_VARIANTS.LARGE}
						component="span"
						isBold
						className="filter-drawer-form__submit-text"
					>
						{resultsCount ? (
							<FormattedMessage
								id="product.filter.submit"
								values={{ count: resultsCount }}
							/>
						) : (
							<FormattedMessage id="product.page.filter.validate.button.labels.plural" />
						)}
					</Typography>
				</Button>
			</footer>
		</Form>
	);
};

FilterDrawerForm.propTypes = {
	isSinglePanelMode: PropTypes.bool.isRequired,
	selectedFilters: PropTypes.objectOf(PropTypes.array).isRequired,
	onReset: PropTypes.func.isRequired,
	onChange: PropTypes.func.isRequired,
	onOpenPanel: PropTypes.func,
	onApply: PropTypes.func,
};
